// frontend/src/components/AnnotateVideo.tsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import KeyboardIcon from '@mui/icons-material/KeyboardOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useAuth } from '../context/AuthContext';
import { addAnnotation, fetchAnnotations, fetchVideoById } from '../graphqlService';
import { brand } from '../theme';
import PageContainer from './ui/PageContainer';
import { getAnnotationCategory } from '../utils/annotationCategories';
import { DEFAULT_FPS, formatTimecode } from '../utils/timecode';
import ProTimeline from './Timeline/ProTimeline';
import type { ProTimelineHandle, TimelineItemChange, TimelineLane } from './Timeline/timelineTypes';
import { useMediaPlayer } from './Timeline/useMediaPlayer';
import TransportBar from './AnnotationStudio/TransportBar';
import VideoStage, { useFullscreen } from './AnnotationStudio/VideoStage';
import QuickComposer from './AnnotationStudio/QuickComposer';
import AnnotationPanel from './AnnotationStudio/AnnotationPanel';
import AnnotationEditDialog from './AnnotationStudio/AnnotationEditDialog';
import ShortcutHelpDialog from './AnnotationStudio/ShortcutHelpDialog';
import SaveStatus from './AnnotationStudio/SaveStatus';
import MarkerExportMenu from './AnnotationStudio/MarkerExportMenu';
import FrameRateChip from './AnnotationStudio/FrameRateChip';
import { useAutosave } from './AnnotationStudio/useAutosave';
import { useStudioTranscriptMarks } from './AnnotationStudio/useStudioTranscriptMarks';
import NotesTranscriptSwitch from './Transcript/NotesTranscriptSwitch';
import TranscriptPanel, { TranscriptLineAction } from './Transcript/TranscriptPanel';
import { markOnDark, studioDark } from './AnnotationStudio/studioTokens';
import {
  StudioAnnotation,
  adjacentAnnotationTime,
  annotationsActiveAt,
  isRangeAnnotation,
  newAnnotationId,
  resolveNoteSpan,
  sortByTime,
} from './AnnotationStudio/annotationModel';

const FPS_STORAGE_PREFIX = 'pithplay.studio.fps.';
const UNTITLED = 'Untitled video';

/** The studio is full width: the page frame's gutters, a tighter top. */
const STUDIO_FRAME_SX = { maxWidth: 1680, pt: { xs: 2, md: 2.5 }, pb: 6 } as const;

/** Square outlined icon buttons in the studio header (back, shortcuts). */
const headerIconButtonSx = {
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: 2,
  border: `1px solid ${brand.borderStrong}`,
  bgcolor: 'background.paper',
  color: brand.slate,
  '&:hover': { bgcolor: brand.ivory },
  '&:focus-visible': { outline: `2px solid ${brand.copper}`, outlineOffset: 2 },
} as const;

/** Read by screen readers, not shown. (Pixel strings: in `sx`, a width of 1 means 100%.) */
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** A browser's own network failure text ("Failed to fetch") means nothing to people. */
const friendlySaveError = (message: string | null | undefined): string => {
  if (!message) return 'Please try again.';
  if (/failed to fetch|networkerror|network request failed|load failed/i.test(message)) {
    return 'Check your connection and try again.';
  }
  return message;
};

const readStoredFps = (videoId?: string): number | null => {
  if (!videoId) return null;
  try {
    const value = Number(window.localStorage.getItem(`${FPS_STORAGE_PREFIX}${videoId}`));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

const storeFps = (videoId: string | undefined, fps: number) => {
  if (!videoId) return;
  try {
    window.localStorage.setItem(`${FPS_STORAGE_PREFIX}${videoId}`, String(fps));
  } catch {
    /* storage unavailable: the choice lasts for this visit only */
  }
};

/** Fields typed into, where single-key shortcuts must not fire. */
const isTypingTarget = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  if (el.isContentEditable) return true;
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return true;
  return Boolean(el.closest('[role="combobox"], [role="listbox"], [role="menu"], [role="dialog"]'));
};

const toPayload = (annotation: StudioAnnotation) => ({
  id: annotation.id,
  time: annotation.time,
  text: annotation.text,
  category: annotation.category,
  color: annotation.color,
  ...(isRangeAnnotation(annotation) ? { endTime: annotation.endTime } : {}),
});

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
  undo?: () => void;
}

/**
 * The creator's annotation studio: a frame-accurate player with an NLE-style
 * transport and timeline, a composer docked under the picture, and the notes
 * list. Notes save automatically (the mutation replaces the whole list).
 */
const AnnotateVideo: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialVideoUrl = (location.state as any)?.videoUrl as string | undefined;
  const initialVideoName = (location.state as any)?.videoName as string | undefined;

  const [videoUrl, setVideoUrl] = useState<string | undefined>(initialVideoUrl);
  const [videoName, setVideoName] = useState<string>(initialVideoName || UNTITLED);
  const [videoInfoLoading, setVideoInfoLoading] = useState<boolean>(!initialVideoUrl);
  // The video's link could not be loaded: say so on the stage, with a retry.
  const [videoInfoFailed, setVideoInfoFailed] = useState(false);
  const [videoInfoNonce, setVideoInfoNonce] = useState(0);
  // The rate read from the file at upload. When it is known it is a fact
  // about the footage and wins; the user's choice (kept per video) only fills
  // in for videos whose rate was never recorded. An old choice made before
  // the rate was known must not hide the real one.
  const [measuredFps, setMeasuredFps] = useState<number | null>(null);
  // False until the video record has loaded, so the chip doesn't flash
  // "estimated" before the real rate arrives.
  const [fpsResolved, setFpsResolved] = useState(false);
  const [fpsOverride, setFpsOverride] = useState<number | null>(() => readStoredFps(videoId));
  const fpsKnown = measuredFps !== null;
  const fps = measuredFps ?? fpsOverride ?? DEFAULT_FPS;
  // The route keeps this component mounted when only the video changes.
  const fpsVideoRef = useRef(videoId);
  useEffect(() => {
    if (fpsVideoRef.current === videoId) return;
    fpsVideoRef.current = videoId;
    setMeasuredFps(null);
    setFpsResolved(false);
    setFpsOverride(readStoredFps(videoId));
  }, [videoId]);

  const [annotations, setAnnotations] = useState<StudioAnnotation[]>([]);
  // Bumped on every user change; drives autosave.
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  // Saving replaces the whole annotation array server-side. If the initial
  // load failed we are holding an empty list that does NOT reflect the server,
  // so saving would silently destroy every existing note. Block it.
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadNonce, setLoadNonce] = useState(0);
  // How many notes the server holds, so emptying the list asks first.
  const savedCountRef = useRef(0);

  const [composerText, setComposerText] = useState('');
  const [composerCategory, setComposerCategory] = useState('general');
  const [inPoint, setInPoint] = useState<number | null>(null);
  const [outPoint, setOutPoint] = useState<number | null>(null);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const leaveAfterSaveRef = useRef(false);
  // Content is kept while the snackbar animates out.
  const [snackbar, setSnackbarState] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const setSnackbar = useCallback((next: Omit<SnackbarState, 'open'> | null) => {
    if (next) setSnackbarState({ ...next, open: true });
    else setSnackbarState((previous) => ({ ...previous, open: false }));
  }, []);

  const player = useMediaPlayer({
    fps,
    loop: { enabled: loopEnabled, start: inPoint, end: outPoint },
  });
  const { seek, pause, getTime } = player;
  const fullscreen = useFullscreen<HTMLDivElement>();
  const timelineRef = useRef<ProTimelineHandle | null>(null);
  const editable = !loading && !loadFailed;

  /** Apply a change made by the user (re-sorted, and queued for autosave). */
  const commit = useCallback((update: (previous: StudioAnnotation[]) => StudioAnnotation[]) => {
    setAnnotations((previous) => sortByTime(update(previous)));
    setVersion((value) => value + 1);
  }, []);

  const saveAnnotations = useCallback(async (list: StudioAnnotation[]) => {
    if (!videoId) throw new Error('No video selected.');
    const token = await user?.getIdToken();
    if (!token) {
      throw new Error('Your session expired. Sign in again — your annotations are still here.');
    }
    await addAnnotation(videoId, list.map(toPayload), token);
    savedCountRef.current = list.length;
  }, [videoId, user]);

  const autosave = useAutosave({
    value: annotations,
    version,
    enabled: Boolean(videoId) && !loading && !loadFailed,
    save: saveAnnotations,
    needsConfirmation: (list) => list.length === 0 && savedCountRef.current > 0,
  });
  const unsaved = autosave.hasUnsavedChanges || autosave.status === 'error';

  // Browser-level guard for tab close / reload while a save is pending or failed.
  useEffect(() => {
    if (!unsaved) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  useEffect(() => {
    if (autosave.status === 'error') {
      setSnackbar({ message: `Couldn't save your notes: ${friendlySaveError(autosave.error)}`, severity: 'error' });
    }
  }, [autosave.status, autosave.error, setSnackbar]);

  // Load annotations once per video (a token refresh must not reload them over edits).
  const loadedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!videoId || !user) return;
    if (loadedForRef.current === `${videoId}:${loadNonce}`) return;
    loadedForRef.current = `${videoId}:${loadNonce}`;
    let cancelled = false;
    let completed = false;
    (async () => {
      try {
        setLoading(true);
        const token = await user.getIdToken(true);
        if (!token) throw new Error('Not signed in');
        const fetched = await fetchAnnotations(videoId, token);
        if (cancelled) return;
        const processed: StudioAnnotation[] = (Array.isArray(fetched) ? fetched : []).map((ann: any) => ({
          id: ann.id || newAnnotationId(),
          time: Number(ann.time) || 0,
          text: String(ann.text ?? ''),
          category: ann.category || 'general',
          color: ann.color || getAnnotationCategory(ann.category).color,
          ...(typeof ann.endTime === 'number' && ann.endTime > ann.time ? { endTime: ann.endTime } : {}),
        }));
        setAnnotations(sortByTime(processed));
        savedCountRef.current = processed.length;
        setLoadFailed(false);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load annotations:', error);
        setLoadFailed(true);
        setSnackbar({
          message: 'Could not load existing annotations. Reload before editing — saving now would erase them.',
          severity: 'error',
        });
      } finally {
        completed = true;
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      // Only an interrupted load is retried (StrictMode re-mounts, a new
      // video); a finished one is never reloaded over the user's edits.
      if (!completed) loadedForRef.current = null;
    };
  }, [videoId, user, loadNonce, setSnackbar]);

  // Video URL, name and frame rate (once per video, again on "Try again").
  const videoInfoForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!videoId) {
      setVideoInfoLoading(false);
      return;
    }
    const key = `${videoId}:${videoInfoNonce}`;
    if (!user || videoInfoForRef.current === key) return;
    videoInfoForRef.current = key;
    const needsUrl = !videoUrl;
    const needsName = videoName === UNTITLED;
    (async () => {
      try {
        if (needsUrl) {
          setVideoInfoLoading(true);
          setVideoInfoFailed(false);
        }
        const token = await user.getIdToken(true);
        if (!token) {
          if (needsUrl) {
            setVideoInfoFailed(true);
            setSnackbar({ message: 'Sign in again to load this video.', severity: 'error' });
          }
          return;
        }
        const fetchedVideo = await fetchVideoById(videoId, token);
        if (needsUrl) {
          if (fetchedVideo?.url) setVideoUrl(fetchedVideo.url);
          else setVideoInfoFailed(true);
        }
        if (fetchedVideo?.name && needsName) setVideoName(fetchedVideo.name);
        const rate = Number(fetchedVideo?.frameRate);
        setMeasuredFps(Number.isFinite(rate) && rate > 0 ? rate : null);
      } catch (error) {
        console.error('Failed to load video:', error);
        if (needsUrl) setVideoInfoFailed(true);
      } finally {
        setVideoInfoLoading(false);
        setFpsResolved(true);
      }
    })();
    // The URL/name from navigation state are read once; refetching on their change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, user, videoInfoNonce, setSnackbar]);

  const duration = player.duration;
  const currentTime = player.currentTime;

  const activeKey = useMemo(
    () => annotationsActiveAt(annotations, currentTime).map((a) => a.id).join('|'),
    [annotations, currentTime]
  );
  const activeIds = useMemo(() => (activeKey ? activeKey.split('|') : []), [activeKey]);

  const stageNotes = useMemo(() => {
    const ids = new Set(activeIds);
    return annotations
      .filter((annotation) => ids.has(annotation.id))
      .map((annotation) => {
        const category = getAnnotationCategory(annotation.category, annotation.color);
        return {
          id: annotation.id,
          label: `${category.label} · ${formatTimecode(annotation.time, fps)}`,
          text: annotation.text,
          color: category.color,
        };
      });
  }, [annotations, activeIds, fps]);

  const lanes = useMemo<TimelineLane[]>(() => [
    {
      id: 'notes',
      label: 'Notes',
      icon: <ChatBubbleOutlineIcon />,
      kind: 'markers',
      editable,
      maxRows: 4,
      emptyText: 'Notes you add appear here. Drag a marker to retime it.',
      items: annotations.map((annotation, index) => {
        const category = getAnnotationCategory(annotation.category, annotation.color);
        return {
          id: annotation.id,
          start: annotation.time,
          end: isRangeAnnotation(annotation) ? (annotation.endTime as number) : null,
          label: annotation.text,
          color: category.color,
          ariaLabel: `Note ${index + 1} (${category.label}): ${annotation.text}`,
          tooltip: (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: markOnDark(category.color), flexShrink: 0 }} />
                <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: studioDark.textMuted }}>
                  {category.label}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.45 }}>{annotation.text}</Typography>
            </Box>
          ),
        };
      }),
    },
  ], [annotations, editable]);

  const scrubMarkers = useMemo(
    () => annotations.map((annotation) => ({
      time: annotation.time,
      endTime: annotation.endTime,
      color: getAnnotationCategory(annotation.category, annotation.color).color,
    })),
    [annotations]
  );

  const handleSeekToAnnotation = useCallback((annotation: StudioAnnotation) => {
    seek(annotation.time);
    setSelectedId(annotation.id);
  }, [seek]);

  const focusComposer = useCallback(() => {
    if (!editable) return;
    pause();
    composerRef.current?.focus();
  }, [editable, pause]);

  // ---- The transcript: Remove, Short and Note from its words -----------------
  const notifyMark = useCallback((message: string, severity: SnackbarState['severity'] = 'success', undo?: () => void) => {
    setSnackbar({
      message,
      severity,
      undo: undo
        ? () => {
            undo();
            setSnackbar(null);
          }
        : undefined,
    });
  }, [setSnackbar]);
  const transcriptMarking = useStudioTranscriptMarks({
    annotations,
    editable,
    loadFailed,
    commit,
    selectedId,
    setSelectedId,
    notify: notifyMark,
    seek,
    onEdit: setEditingId,
  });

  /** A note on words in the transcript: it covers them (in and out marks), quotes them, and the composer opens. */
  const handleAddNoteFromTranscript = useCallback((line: TranscriptLineAction) => {
    if (!editable) return;
    seek(line.start);
    setInPoint(line.start);
    setOutPoint(line.end > line.start ? line.end : null);
    setComposerText((current) => (current.trim() ? current : `“${line.text}” — `));
    focusComposer();
  }, [editable, focusComposer, seek]);

  const markIn = useCallback(() => {
    const time = getTime();
    setInPoint(time);
    setOutPoint((out) => (out !== null && out <= time ? null : out));
  }, [getTime]);

  const markOut = useCallback(() => {
    const time = getTime();
    setOutPoint(time);
    setInPoint((current) => (current !== null && current >= time ? null : current));
  }, [getTime]);

  const clearMarks = useCallback(() => {
    setInPoint(null);
    setOutPoint(null);
    setLoopEnabled(false);
  }, []);

  const handleAddNote = () => {
    const text = composerText.trim();
    if (!text || !editable) return;
    const playhead = player.getTime();
    const span = resolveNoteSpan(inPoint, outPoint, playhead, 1 / fps);
    const limit = duration > 0 ? duration : Number.POSITIVE_INFINITY;
    const time = Math.min(span.time, limit);
    const endTime = typeof span.endTime === 'number' ? Math.min(span.endTime, limit) : undefined;
    const category = getAnnotationCategory(composerCategory);
    const note: StudioAnnotation = {
      id: newAnnotationId(),
      time,
      text,
      category: composerCategory,
      color: category.color,
      ...(typeof endTime === 'number' && endTime > time ? { endTime } : {}),
    };
    commit((previous) => [...previous, note]);
    setComposerText('');
    clearMarks();
    setSelectedId(note.id);
  };

  const handleTimelineChange = useCallback((item: { id: string }, _lane: unknown, next: TimelineItemChange) => {
    commit((previous) => previous.map((annotation) => {
      if (annotation.id !== item.id) return annotation;
      const updated: StudioAnnotation = { ...annotation, time: next.start };
      if (next.end !== null && next.end > next.start) updated.endTime = next.end;
      else delete updated.endTime;
      return updated;
    }));
  }, [commit]);

  const editingAnnotation = useMemo(
    () => annotations.find((annotation) => annotation.id === editingId) ?? null,
    [annotations, editingId]
  );

  const handleSaveEdit = (next: { text: string; category: string; time: number; endTime?: number }) => {
    if (!editingId) return;
    const category = getAnnotationCategory(next.category);
    commit((previous) => previous.map((annotation) => {
      if (annotation.id !== editingId) return annotation;
      const updated: StudioAnnotation = {
        ...annotation,
        text: next.text,
        category: next.category,
        color: category.known ? category.color : annotation.color,
        time: next.time,
      };
      if (typeof next.endTime === 'number' && next.endTime > next.time) updated.endTime = next.endTime;
      else delete updated.endTime;
      return updated;
    }));
    setEditingId(null);
  };

  const handleConfirmDelete = () => {
    const target = annotations.find((annotation) => annotation.id === deleteConfirmId);
    setDeleteConfirmId(null);
    if (!target) return;
    commit((previous) => previous.filter((annotation) => annotation.id !== target.id));
    if (selectedId === target.id) setSelectedId(null);
    setSnackbar({
      message: 'Annotation deleted',
      severity: 'info',
      undo: () => {
        commit((previous) => (previous.some((a) => a.id === target.id) ? previous : [...previous, target]));
        setSnackbar(null);
      },
    });
  };

  const goToLibrary = () => navigate('/library');

  const handleBack = () => {
    if (unsaved || autosave.status === 'saving') {
      setLeaveConfirmOpen(true);
      return;
    }
    goToLibrary();
  };

  const handleSaveNow = async () => {
    const ok = await autosave.saveNow();
    if (ok && leaveAfterSaveRef.current) {
      leaveAfterSaveRef.current = false;
      goToLibrary();
    }
    return ok;
  };

  const handleSaveAndLeave = async () => {
    setLeaveConfirmOpen(false);
    leaveAfterSaveRef.current = true;
    const ok = await autosave.saveNow();
    if (ok) {
      leaveAfterSaveRef.current = false;
      goToLibrary();
    }
  };

  const handleConfirmEmptySave = async () => {
    const ok = await autosave.saveNow({ confirmed: true });
    if (ok && leaveAfterSaveRef.current) {
      leaveAfterSaveRef.current = false;
      goToLibrary();
    }
  };

  const anyDialogOpen =
    helpOpen || leaveConfirmOpen || Boolean(editingId) || Boolean(deleteConfirmId) || autosave.status === 'needs-confirmation';

  // Keyboard shortcuts, read through a ref so the listener is bound once.
  const shortcutRef = useRef<(event: KeyboardEvent) => void>(() => undefined);
  shortcutRef.current = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    if (anyDialogOpen || isTypingTarget(event.target)) return;
    if (!player.element && event.code !== 'Slash') return;

    if (event.key === '?') {
      event.preventDefault();
      setHelpOpen(true);
      return;
    }
    const target = event.target as HTMLElement | null;
    const nativeControl = target && ['BUTTON', 'A'].includes(target.tagName);

    switch (event.code) {
      case 'Space':
        if (nativeControl) return;
        event.preventDefault();
        player.toggle();
        break;
      case 'KeyK':
        event.preventDefault();
        player.pause();
        break;
      case 'KeyJ':
        event.preventDefault();
        player.shuttleReverse();
        break;
      case 'KeyL':
        event.preventDefault();
        if (event.shiftKey) {
          if (inPoint !== null && outPoint !== null) setLoopEnabled((value) => !value);
        } else {
          player.shuttleForward();
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        player.skip(event.shiftKey ? -10 : -5);
        break;
      case 'ArrowRight':
        event.preventDefault();
        player.skip(event.shiftKey ? 10 : 5);
        break;
      case 'ArrowUp':
        event.preventDefault();
        player.setVolume(player.volume + 0.1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        player.setVolume(player.volume - 0.1);
        break;
      case 'Comma':
        event.preventDefault();
        player.stepFrames(-1);
        break;
      case 'Period':
        event.preventDefault();
        player.stepFrames(1);
        break;
      case 'Home':
        event.preventDefault();
        player.seek(0);
        break;
      case 'End':
        event.preventDefault();
        player.seek(duration);
        break;
      case 'KeyM':
        event.preventDefault();
        player.toggleMute();
        break;
      case 'KeyF':
        event.preventDefault();
        fullscreen.toggle();
        break;
      case 'KeyA':
      case 'Insert':
        event.preventDefault();
        focusComposer();
        break;
      case 'KeyI':
        event.preventDefault();
        markIn();
        break;
      case 'KeyO':
        event.preventDefault();
        markOut();
        break;
      case 'Escape':
        clearMarks();
        setSelectedId(null);
        break;
      case 'BracketLeft':
      case 'BracketRight': {
        event.preventDefault();
        const next = adjacentAnnotationTime(annotations, player.getTime(), event.code === 'BracketRight' ? 1 : -1);
        if (next !== null) {
          player.seek(next);
          const match = annotations.find((annotation) => annotation.time === next);
          if (match) setSelectedId(match.id);
        }
        break;
      }
      case 'Equal':
      case 'NumpadAdd':
        event.preventDefault();
        timelineRef.current?.zoomIn();
        break;
      case 'Minus':
      case 'NumpadSubtract':
        event.preventDefault();
        timelineRef.current?.zoomOut();
        break;
      case 'Backslash':
        event.preventDefault();
        timelineRef.current?.fit();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const listener = (event: KeyboardEvent) => shortcutRef.current(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  if (loading && !loadFailed && annotations.length === 0 && version === 0) {
    return (
      <PageContainer width="full" sx={STUDIO_FRAME_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={120} height={18} />
            <Skeleton variant="text" width="40%" height={30} />
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' }, gap: 2.5 }}>
          <Box>
            <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 3, mb: 2 }} />
            <Skeleton variant="rectangular" height={96} sx={{ borderRadius: 3, mb: 2 }} />
            <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3 }} />
          </Box>
          <Skeleton variant="rectangular" height={560} sx={{ borderRadius: 3 }} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="full" sx={STUDIO_FRAME_SX}>
      {/* Header: the studio's own full-bleed style (no PageHeader) */}
      <Box
        component="header"
        sx={{ display: 'flex', alignItems: 'center', columnGap: 1.5, rowGap: 1.25, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}
      >
        <Tooltip title="Back to library">
          <IconButton onClick={handleBack} aria-label="Back to library" sx={headerIconButtonSx}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" component="p" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.02em', lineHeight: 1.5 }}>
            Annotation studio
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
            <Typography
              variant="h5"
              component="h1"
              title={videoName}
              sx={{ fontWeight: 700, fontSize: { xs: 18, md: 21 }, lineHeight: 1.3, minWidth: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {videoName}
            </Typography>
            {duration > 0 && (
              <Chip
                size="small"
                label={(
                  <>
                    <Box component="span" sx={visuallyHidden}>Length </Box>
                    {formatTimecode(duration, fps)}
                  </>
                )}
                sx={{ height: 24, fontVariantNumeric: 'tabular-nums' }}
              />
            )}
            {fpsResolved && (
              <FrameRateChip
                fps={fps}
                known={fpsKnown}
                chosen={fpsOverride !== null}
                onChange={(rate) => {
                  setFpsOverride(rate);
                  storeFps(videoId, rate);
                }}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.5, rowGap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
          <SaveStatus
            status={autosave.status}
            error={autosave.error ? friendlySaveError(autosave.error) : autosave.error}
            lastSavedAt={autosave.lastSavedAt}
            hasUnsavedChanges={autosave.hasUnsavedChanges}
            onSaveNow={() => void handleSaveNow()}
            blocked={loadFailed}
          />
          <MarkerExportMenu
            annotations={annotations}
            fps={fps}
            fpsAssumed={!fpsKnown && fpsOverride === null}
            title={videoName}
            videoId={videoId}
            sourceName={videoName}
            onExported={(filename) => setSnackbar({ message: `Exported ${filename}`, severity: 'success' })}
          />
          {/* Phones have no keyboard to use the shortcuts with. */}
          <Tooltip title="Keyboard shortcuts (?)">
            <IconButton
              aria-label="Keyboard shortcuts"
              onClick={() => setHelpOpen(true)}
              sx={{ ...headerIconButtonSx, display: { xs: 'none', sm: 'inline-flex' } }}
            >
              <KeyboardIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loadFailed && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button color="inherit" size="small" onClick={() => setLoadNonce((value) => value + 1)}>
              Reload
            </Button>
          )}
        >
          Could not load existing annotations. Reload before editing — saving now would erase them.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px', xl: 'minmax(0, 1fr) 420px' }, gap: 2.5, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {/* Viewer */}
          <Box
            ref={fullscreen.ref}
            sx={{
              borderRadius: fullscreen.isFullscreen ? 0 : 3,
              overflow: 'hidden',
              bgcolor: studioDark.stage,
              display: 'flex',
              flexDirection: 'column',
              ...(fullscreen.isFullscreen ? { height: '100vh' } : {}),
            }}
          >
            <VideoStage
              videoUrl={videoUrl}
              player={player}
              testId="annotation-video-player"
              notes={stageNotes}
              fullscreen={fullscreen.isFullscreen}
              maxHeight="min(50vh, 620px)"
              badge={(inPoint !== null || outPoint !== null) ? (
                <Box
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    bgcolor: studioDark.overlay,
                    borderLeft: `3px solid ${studioDark.accent}`,
                    color: studioDark.text,
                    fontSize: 12,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'pre',
                  }}
                >
                  {inPoint !== null ? `In ${formatTimecode(inPoint, fps)}` : 'In —'}
                  {'  ·  '}
                  {outPoint !== null ? `Out ${formatTimecode(outPoint, fps)}` : 'Out —'}
                </Box>
              ) : undefined}
              placeholder={(
                <Box
                  data-testid="annotation-video-missing"
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, px: 3, textAlign: 'center', color: studioDark.textMuted }}
                >
                  {videoInfoLoading ? (
                    <>
                      <CircularProgress size={28} sx={{ color: studioDark.textMuted }} />
                      <Typography variant="body2">Loading video…</Typography>
                    </>
                  ) : videoInfoFailed ? (
                    <>
                      <Typography variant="body2" sx={{ color: studioDark.text, fontWeight: 600 }}>
                        Couldn’t load this video
                      </Typography>
                      <Typography variant="body2">Your notes are safe. Try again, or go back to the library.</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setVideoInfoNonce((value) => value + 1)}
                        sx={{
                          color: studioDark.text,
                          borderColor: studioDark.rail,
                          '&:hover': { borderColor: studioDark.textMuted, bgcolor: studioDark.hover },
                          '&:focus-visible': { outline: `2px solid ${studioDark.focusRing}`, outlineOffset: 2 },
                        }}
                      >
                        Try again
                      </Button>
                    </>
                  ) : (
                    <Typography variant="body2">No video selected</Typography>
                  )}
                </Box>
              )}
            />
            <TransportBar
              player={player}
              fps={fps}
              disabled={!videoUrl}
              markers={scrubMarkers}
              inPoint={inPoint}
              outPoint={outPoint}
              loop={{
                enabled: loopEnabled,
                available: inPoint !== null && outPoint !== null && outPoint > inPoint,
                onToggle: () => setLoopEnabled((value) => !value),
              }}
              isFullscreen={fullscreen.isFullscreen}
              onToggleFullscreen={fullscreen.toggle}
            />
          </Box>

          <QuickComposer
            fps={fps}
            playheadTime={currentTime}
            inPoint={inPoint}
            outPoint={outPoint}
            category={composerCategory}
            onCategoryChange={setComposerCategory}
            text={composerText}
            onTextChange={setComposerText}
            onSubmit={handleAddNote}
            onMarkIn={markIn}
            onMarkOut={markOut}
            onClearIn={() => { setInPoint(null); setLoopEnabled(false); }}
            onClearOut={() => { setOutPoint(null); setLoopEnabled(false); }}
            onFocus={() => player.pause()}
            disabled={!editable || !videoUrl}
            inputRef={composerRef}
          />

          <ProTimeline
            ref={timelineRef}
            title="Timeline"
            duration={duration}
            currentTime={currentTime}
            playing={player.playing}
            fps={fps}
            lanes={lanes}
            selectedItemId={selectedId}
            inPoint={inPoint}
            outPoint={outPoint}
            onSeek={player.seek}
            onItemSelect={(item) => setSelectedId(item.id)}
            onItemActivate={(item) => editable && setEditingId(item.id)}
            onItemChange={editable ? handleTimelineChange : undefined}
            emptyMessage="The timeline appears once the video's length is known."
            data-testid="studio-timeline"
          />
        </Box>

        <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 }, minWidth: 0 }}>
          <NotesTranscriptSwitch
            notesLabel="Notes"
            notes={(
              <AnnotationPanel
                annotations={annotations}
                fps={fps}
                activeIds={activeIds}
                selectedId={selectedId}
                playing={player.playing}
                readOnly={!editable}
                onSeek={handleSeekToAnnotation}
                onEdit={(id) => setEditingId(id)}
                onDelete={(id) => setDeleteConfirmId(id)}
                listMaxHeight={videoId ? 'calc(100vh - 380px)' : 'calc(100vh - 330px)'}
              />
            )}
            transcript={videoId ? (
              <TranscriptPanel
                videoId={videoId}
                currentTime={currentTime}
                playing={player.playing}
                fps={fps}
                onSeek={seek}
                canStart
                exportName={videoName}
                onAddNote={editable ? handleAddNoteFromTranscript : undefined}
                marking={transcriptMarking}
                listMaxHeight="calc(100vh - 380px)"
                testId="studio-transcript-panel"
              />
            ) : null}
            testId="studio-notes-transcript-switch"
          />
        </Box>
      </Box>

      <AnnotationEditDialog
        open={Boolean(editingAnnotation)}
        annotation={editingAnnotation}
        fps={fps}
        duration={duration}
        getPlayhead={player.getTime}
        onCancel={() => setEditingId(null)}
        onSave={handleSaveEdit}
      />

      <ShortcutHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Unsaved changes when leaving */}
      <Dialog open={leaveConfirmOpen} onClose={() => setLeaveConfirmOpen(false)} maxWidth="xs" fullWidth aria-labelledby="leave-title">
        <DialogTitle id="leave-title" sx={{ fontWeight: 700 }}>Leave without saving?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {autosave.status === 'error'
              ? 'Your latest changes could not be saved.'
              : 'Your latest annotation changes haven’t been saved yet.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setLeaveConfirmOpen(false)}>Keep editing</Button>
          <Button
            color="error"
            onClick={() => {
              setLeaveConfirmOpen(false);
              autosave.discard();
              goToLibrary();
            }}
          >
            Leave without saving
          </Button>
          <Button variant="contained" disabled={autosave.status === 'saving' || loadFailed} onClick={() => void handleSaveAndLeave()}>
            Save and leave
          </Button>
        </DialogActions>
      </Dialog>

      {/* Saving an empty list removes every annotation */}
      <Dialog
        open={autosave.status === 'needs-confirmation'}
        onClose={() => {
          leaveAfterSaveRef.current = false;
          autosave.dismissConfirmation();
        }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="remove-all-title"
      >
        <DialogTitle id="remove-all-title" sx={{ fontWeight: 700 }}>Remove every annotation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            There are no annotations left, so saving removes every annotation on this video.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              leaveAfterSaveRef.current = false;
              autosave.dismissConfirmation();
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={() => void handleConfirmEmptySave()}>
            Remove all and save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteConfirmId)} onClose={() => setDeleteConfirmId(null)} maxWidth="xs" fullWidth aria-labelledby="delete-title">
        <DialogTitle id="delete-title" sx={{ fontWeight: 700 }}>Delete annotation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This annotation will be removed from the video. You can undo right after deleting.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteConfirmId(null)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.undo ? 6000 : 4000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSnackbar(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar(null)}
          severity={snackbar.severity}
          variant="filled"
          action={snackbar.undo ? (
            <Button color="inherit" size="small" onClick={snackbar.undo} sx={{ fontWeight: 700 }}>
              Undo
            </Button>
          ) : undefined}
          sx={{ borderRadius: 2, alignItems: 'center' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default AnnotateVideo;
